import {
  DateInput,
  Modal,
  NumberInput,
  SelectInput,
  TextField,
} from 'melp-design/components';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { Stack } from '@mui/material';
import { useAdminShopItem } from 'store/admin-shop';
import { getSinglePriceLabel } from 'components/marketplace/ItemCard/common/getPriceLabel';
import {
  useAdminShopVoucher,
  useUpdateShopVoucher,
} from 'store/admin-shop-vouchers';
import { fromCents, toCents } from 'utils/general';

interface Values {
  shopItemPriceId: string;
  purchasePrice: number;
  purchaseReference: string;
  expiresAt: string | null;
}

const INITIAL_VALUES: Values = {
  shopItemPriceId: '',
  purchasePrice: NaN,
  purchaseReference: '',
  expiresAt: null,
};

interface Props extends ModalProps {
  shopItemId: string;
  voucherId: string;
}

export const EditFileVoucherModal: FC<Props> = ({
  shopItemId,
  voucherId,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit, reset } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const { data: shopItem, isLoading: isShopItemLoading } =
    useAdminShopItem(shopItemId);
  const { data: voucher, isLoading: isVoucherLoading } =
    useAdminShopVoucher(voucherId);

  useEffect(() => {
    if (voucher) {
      reset({
        shopItemPriceId: voucher.shopItem.priceId,
        purchasePrice: fromCents(voucher.purchasePrice),
        purchaseReference: voucher.purchaseReference,
        expiresAt: voucher.expiresAt,
      });
    }
  }, [reset, voucher]);

  const { mutate: updateShopVoucher, isLoading } = useUpdateShopVoucher();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateShopVoucher(
      {
        voucherId,
        data: {
          shopItemPriceId: values.shopItemPriceId,
          purchaseReference: values.purchaseReference,
          purchasePrice: toCents(values.purchasePrice),
          expiresAt: values.expiresAt,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          closeModal({ action: 'CLOSE' });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={`${t('common.edit')}: ${voucher?.sequence ?? ''}`}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: isLoading || isShopItemLoading || isVoucherLoading,
          },
        ]}
      >
        <Stack gap="20px">
          <Controller
            name="shopItemPriceId"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SelectInput
                label={t('marketplace.ordersTablePrice')}
                name={name}
                value={value}
                onChange={onChange}
                options={
                  shopItem?.prices
                    .filter((price) => price.priceType === 'fixedPrice')
                    .map((price) => ({
                      label: getSinglePriceLabel(price, shopItem.currency),
                      value: price.id,
                    })) ?? []
                }
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="purchasePrice"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <NumberInput
                label={t('shop.vouchers.purchasePrice.label')}
                name={name}
                value={value}
                onChange={onChange}
                required
                key={value}
                InputProps={{
                  endAdornment: voucher?.currency,
                }}
                ref={ref}
              />
            )}
          />

          <Controller
            name="purchaseReference"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <TextField
                  label={t('shop.vouchers.purchaseReference.label')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="expiresAt"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <DateInput
                label={t('alternatives.employeeExpirationDate')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
